import React, { ReactNode, useState } from 'react'
import { motion } from 'framer-motion'
import { 
  CloudArrowUpIcon,
  UserCircleIcon, 
  ArrowRightOnRectangleIcon 
} from '@heroicons/react/24/outline'
import { useAuth } from '../contexts/AuthContext'
import { useNotifications } from '../contexts/NotificationContext'
import { useBranding } from '../hooks/useBranding'
import Footer from './Footer'
import EULAModal from './EULAModal'
import TermsModal from './TermsModal'
import NotificationCenter from './NotificationCenter'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const { user, signOut, isAdmin, isManager } = useAuth()
  const { notifications, removeNotification, clearAllNotifications } = useNotifications()
  const { branding } = useBranding()
  const [isEULAOpen, setIsEULAOpen] = useState(false)
  const [isTermsOpen, setIsTermsOpen] = useState(false)
  // Fall back to the default icon + wordmark if the custom logo fails to load.
  const [logoFailed, setLogoFailed] = useState(false)

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200 relative z-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo and brand */}
            <div className="flex items-center">
              <motion.div 
                className="flex items-center space-x-3"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                {branding.logoConfigured && branding.logoUrl && !logoFailed ? (
                  <img
                    src={branding.logoUrl}
                    alt="S3 Portal"
                    className="h-9 w-auto object-contain"
                    onError={() => setLogoFailed(true)}
                  />
                ) : (
                  <>
                    <div className="flex-shrink-0">
                      <CloudArrowUpIcon className="h-8 w-8 text-primary-600" />
                    </div>
                    <div>
                      <h1 className="text-xl font-bold text-gray-900">S3 Portal</h1>
                      <p className="text-xs text-gray-500">Enterprise File Management</p>
                    </div>
                  </>
                )}
              </motion.div>
            </div>

            {/* User menu */}
            <div className="flex items-center space-x-4">
              {/* User info */}
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900 max-w-[180px] truncate" title={user?.signInDetails?.loginId || user?.username}>
                    {user?.signInDetails?.loginId || user?.username}
                  </p>
                  <p className="text-xs text-gray-500">
                    {isAdmin ? 'Administrator' : isManager ? 'Manager' : 'User'}
                  </p>
                </div>
                <UserCircleIcon className="h-8 w-8 text-gray-400" />
              </div>

              {/* Notification Center */}
              <NotificationCenter 
                notifications={notifications}
                onDismiss={removeNotification}
                onClearAll={clearAllNotifications}
              />

              {/* Sign out button */}
              <motion.button
                onClick={signOut}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
                <span>Sign Out</span>
              </motion.button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="flex-1 max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 w-full">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {children}
        </motion.div>
      </main>

      {/* Footer */}
      <Footer 
        onEULAClick={() => setIsEULAOpen(true)}
        onTermsClick={() => setIsTermsOpen(true)}
      />

      {/* Modals */}
      <EULAModal 
        isOpen={isEULAOpen} 
        onClose={() => setIsEULAOpen(false)} 
      />
      <TermsModal 
        isOpen={isTermsOpen} 
        onClose={() => setIsTermsOpen(false)} 
      />
    </div>
  )
}
